/**
 * useMediaLibrary — efeitos e ações da biblioteca de imagens (contrato `mediaLibrary.vm.js` @v1).
 *
 * As REGRAS moram em `mediaLibraryState.js` (redutor puro) e a TRADUÇÃO em `mediaLibraryVmBuilder.js`; aqui fica só o
 * que precisa de React: carregar ao abrir, a fila de envios (até 3 juntos), as chamadas de descrever/apagar e o
 * VOLTAR do celular fechando a janela em tela cheia (DESIGN-FLOW §3, item 15).
 *
 * @param {object} p
 * @param {boolean} p.open
 * @param {'gallery'|'logo'} p.mode
 * @param {string[]} [p.jaNaGaleria]   assetIds que a galeria já tem (não podem ser escolhidos de novo)
 * @param {number} [p.vagas]           quantas ainda cabem na galeria (30 − as que já estão)
 * @param {string|null} [p.logoAtual]  assetId do logo atual (modo logo)
 * @param {string|null} [p.nomeDoRamo] 'Barbearia' — título dos exemplos
 * @param {(itens: object[]) => void} p.onConfirm  recebe as fotos escolhidas, na ordem, no formato da Mídia
 * @param {() => void} p.onClose
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import mediaService from '../../services/mediaService';
import { useCloseOnBack } from '../../hooks/useCloseOnBack';
import { reduzir, estadoInicial, escolhidas } from './mediaLibraryState';
import { montarVm, itemDoEnvio } from './mediaLibraryVmBuilder';

/** Depois que uma foto sobe, a Mídia a lista quando o FileService avisa pela fila — relê a lista depois disso. */
const RELER_DEPOIS_DO_ENVIO_MS = 2500;

const ETAPA = { preparando: 'preparing', enviando: 'uploading', processando: 'processing' };

export default function useMediaLibrary({
  open, mode = 'gallery', jaNaGaleria = [], vagas, logoAtual = null, nomeDoRamo = null, onConfirm, onClose,
}) {
  const [s, dispatch] = useReducer(reduzir, estadoInicial);
  const theme = useTheme();
  const telaCheia = useMediaQuery(theme.breakpoints.down('sm'));

  const fila = useRef(null);
  if (!fila.current) fila.current = mediaService.criarFila();
  const arquivos = useRef(new Map());        // id do envio → { arquivo, previewUrl } (para tentar de novo e soltar o blob)
  const reler = useRef(null);
  const aberta = useRef(open);
  aberta.current = open;

  useCloseOnBack(open && telaCheia, onClose);

  const carregar = useCallback(async () => {
    try {
      dispatch({ type: 'CARREGOU', pagina: await mediaService.listar(), mais: false });
    } catch (e) {
      dispatch({ type: 'FALHOU_CARREGAR', mensagem: e.message, mais: false });
    }
  }, []);

  const soltarArquivos = useCallback(() => {
    arquivos.current.forEach(({ previewUrl }) => previewUrl && URL.revokeObjectURL(previewUrl));
    arquivos.current.clear();
  }, []);

  // Abrir zera a escolha e relê (a lista pode ter mudado em outra aba); fechar solta as miniaturas locais.
  useEffect(() => {
    if (!open) return undefined;
    dispatch({ type: 'ABRIR', mode, vagas, jaNaGaleria, logoAtual });
    carregar();
    return () => {
      clearTimeout(reler.current);
      soltarArquivos();
    };
    // Só a abertura importa: vagas/jaNaGaleria são fotografados no momento em que a janela abre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const enviar = useCallback((id, arquivo) => {
    fila.current.adicionar(() => mediaService.enviarFoto(arquivo, {
      aoMudar: ({ etapa, progresso }) => dispatch({ type: 'ENVIO_ETAPA', id, etapa: ETAPA[etapa] ?? etapa, progresso }),
    }))
      .then((r) => {
        if (!aberta.current) return;
        if (r.tipo === 'jaExiste') {
          dispatch({ type: 'ENVIO_PRONTO', id, item: r.imagem, duplicado: true });
          return;
        }
        dispatch({ type: 'ENVIO_PRONTO', id, item: itemDoEnvio(r), duplicado: false });
        clearTimeout(reler.current);
        reler.current = setTimeout(() => aberta.current && carregar(), RELER_DEPOIS_DO_ENVIO_MS);
      })
      .catch((e) => aberta.current && dispatch({ type: 'ENVIO_FALHOU', id, codigo: e.codigo, mensagem: e.message }));
  }, [carregar]);

  const actions = useMemo(() => ({
    onClose,
    onRetry: () => { dispatch({ type: 'ABRIR', mode, vagas, jaNaGaleria, logoAtual }); carregar(); },
    onLoadMore: async () => {
      if (!s.proximo || s.carregandoMais) return;
      dispatch({ type: 'CARREGANDO_MAIS' });
      try {
        dispatch({ type: 'CARREGOU', pagina: await mediaService.listar(s.proximo), mais: true });
      } catch (e) {
        dispatch({ type: 'FALHOU_CARREGAR', mensagem: e.message, mais: true });
      }
    },
    onUploadFiles: (files) => {
      Array.from(files ?? []).forEach((arquivo) => {
        const id = crypto.randomUUID();
        let previewUrl = null;
        try { previewUrl = URL.createObjectURL(arquivo); } catch { /* sem miniatura local */ }
        arquivos.current.set(id, { arquivo, previewUrl });
        dispatch({ type: 'ENVIO_NA_FILA', id, nome: arquivo.name, previewUrl });
        enviar(id, arquivo);
      });
    },
    onRetryUpload: (id) => {
      const guardado = arquivos.current.get(id);
      if (!guardado) return;
      dispatch({ type: 'ENVIO_REPETIR', id });
      enviar(id, guardado.arquivo);
    },
    onDismissUpload: (id) => {
      const guardado = arquivos.current.get(id);
      if (guardado?.previewUrl) URL.revokeObjectURL(guardado.previewUrl);
      arquivos.current.delete(id);
      dispatch({ type: 'ENVIO_DISPENSAR', id });
    },
    onToggleSelect: (id) => dispatch({ type: 'ALTERNAR', id }),
    onConfirm: () => {
      const fotos = escolhidas(s);
      if (fotos.length) onConfirm?.(fotos);
    },
    onOpenDescribe: (id) => dispatch({ type: 'DESCRICAO_ABRIR', id }),
    onChangeDescription: (valor) => dispatch({ type: 'DESCRICAO_MUDAR', valor }),
    onSaveDescription: async () => {
      const { itemId, valor } = s.descricao;
      if (!itemId || s.descricao.salvando) return;
      dispatch({ type: 'DESCRICAO_SALVANDO' });
      try {
        await mediaService.descrever(itemId, valor);
        dispatch({ type: 'DESCRICAO_SALVA', id: itemId, alt: valor.trim() });
      } catch (e) {
        dispatch({ type: 'DESCRICAO_FALHOU', mensagem: e.message });
      }
    },
    onCloseDescribe: () => dispatch({ type: 'DESCRICAO_FECHAR' }),
    onAskDelete: (id) => dispatch({ type: 'APAGAR_PERGUNTAR', id }),
    onConfirmDelete: async () => {
      const { itemId, emUso, apagando } = s.apagar;
      if (!itemId || apagando) return;
      dispatch({ type: 'APAGAR_ENVIANDO' });
      try {
        // Só confirma o apagar-em-uso quando a pessoa VIU o aviso de peso (emUso > 0 no diálogo) — M37.
        await mediaService.apagar(itemId, (emUso ?? 0) > 0);
        dispatch({ type: 'APAGADA', id: itemId });
      } catch (e) {
        if (e.codigo === 'EM_USO') dispatch({ type: 'APAGAR_EM_USO', usadoEm: e.usadoEm ?? 1 });
        else dispatch({ type: 'APAGAR_FALHOU', mensagem: e.message });
      }
    },
    onCloseDelete: () => dispatch({ type: 'APAGAR_FECHAR' }),
    onDismissNotice: () => dispatch({ type: 'AVISO_FECHAR' }),
  }), [s, onClose, onConfirm, mode, vagas, jaNaGaleria, logoAtual, carregar, enviar]);

  const vm = useMemo(() => montarVm({
    ...s,
    open,
    nomeDoRamo,
    telaCheia,
  }), [s, open, nomeDoRamo, telaCheia]);

  return { vm, actions };
}
